import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { brand } from "../../../theme/businessFlowsTheme";

export const moveItem = (list, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

// A list reordered by dragging its handle, or with the arrow buttons for
// keyboard and touch users. Only the handle starts a drag, so text fields in
// a row stay selectable. When onReorder answers a promise (a save), further
// moves are ignored until it settles, so two orders never race.
export default function SortableList({ items, getKey, getLabel = getKey, onReorder, disabled = false, renderItem, gap = 1 }) {
  const [armed, setArmed] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  // A handle pressed and released elsewhere (or the window losing focus)
  // must not leave its row draggable.
  useEffect(() => {
    if (armed === null) return undefined;
    const disarm = () => setArmed(null);
    window.addEventListener("mouseup", disarm);
    window.addEventListener("blur", disarm);
    return () => {
      window.removeEventListener("mouseup", disarm);
      window.removeEventListener("blur", disarm);
    };
  }, [armed]);

  const finish = () => {
    setArmed(null);
    setDragging(null);
    setOver(null);
  };

  const move = (from, to) => {
    if (busy) return;
    const next = moveItem(items, from, to);
    if (next === items) return;
    const pending = onReorder(next);
    if (pending && typeof pending.then === "function") {
      setBusy(true);
      Promise.resolve(pending)
        .catch(() => {})
        .then(() => {
          if (mounted.current) setBusy(false);
        });
    }
  };

  return (
    <Stack component="ol" spacing={gap} aria-busy={busy || undefined} sx={{ listStyle: "none", m: 0, p: 0 }}>
      {items.map((item, index) => (
        <Box
          component="li"
          key={getKey(item)}
          draggable={!disabled && armed === index}
          onDragStart={(e) => {
            setDragging(index);
            e.dataTransfer.effectAllowed = "move";
          }}
          onDragOver={(e) => {
            if (dragging === null) return;
            e.preventDefault();
            setOver(index);
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (dragging !== null && dragging !== index) move(dragging, index);
            finish();
          }}
          onDragEnd={finish}
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: 0.5,
            borderRadius: 3,
            outline: over === index && dragging !== index ? `2px dashed ${brand.inkSoft}` : "none",
            opacity: dragging === index ? 0.5 : 1,
          }}
        >
          {!disabled && (
            <Stack alignItems="center" justifyContent="center" sx={{ color: "text.secondary", flexShrink: 0 }}>
              <Box
                onMouseDown={() => setArmed(index)}
                onMouseUp={() => setArmed(null)}
                aria-hidden
                sx={{ cursor: "grab", display: "grid", placeItems: "center", py: 0.25 }}
              >
                <DragIndicatorRoundedIcon fontSize="small" />
              </Box>
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" aria-label={`Move ${getLabel(item)} up`} disabled={index === 0} onClick={() => move(index, index - 1)}>
                    <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Move ${getLabel(item)} down`}
                    disabled={index === items.length - 1}
                    onClick={() => move(index, index + 1)}
                  >
                    <ArrowDownwardRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>{renderItem(item, index)}</Box>
        </Box>
      ))}
    </Stack>
  );
}
